import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import Logo from "../components/Logo";

/**
 * PAGE ÉDITO — La Vision de la Direction
 * Texte intégral fourni par la fondatrice le 24/02/2026. À NE PAS RÉÉCRIRE.
 */
export default function Edito() {
  const { lang } = useI18n();

  const isFR = lang === "fr";

  const P = ({ children }) => (
    <p className="text-[1.02rem] leading-[1.85] text-[var(--gai-grey-800)]">{children}</p>
  );
  const H2 = ({ children }) => (
    <h2 className="serif text-3xl md:text-4xl text-[var(--gai-black)] mt-14 mb-6">{children}</h2>
  );

  return (
    <div className="bg-[var(--gai-white)]" data-testid="page-edito">
      {/* HERO */}
      <div className="border-b border-[var(--gai-grey-200)]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-16 pb-14 text-center">
          <div className="overline mb-4 text-[var(--gai-red)]">
            {isFR ? "Édito · La Vision de la Direction" : "Editorial · Leadership Vision"}
          </div>
          <h1 className="serif text-4xl md:text-6xl leading-[1.05] text-[var(--gai-black)]">
            {isFR ? "Bienvenue dans " : "Welcome to "}
            <span className="text-[var(--gai-red)]" translate="no">Global Afrique International</span>
          </h1>
          <p className="mt-5 text-sm md:text-base text-[var(--gai-grey-600)] max-w-2xl mx-auto">
            {isFR
              ? "Le Magazine d'Affaires Panafricain"
              : "The Pan-African Business Magazine"}
          </p>
          <div className="mt-10 flex justify-center">
            <div className="h-[1px] w-24 bg-[var(--gai-red)]" />
          </div>
        </div>
      </div>

      {/* BODY */}
      <article className="max-w-[820px] mx-auto px-6 lg:px-10 py-16" data-testid="edito-body">
        {isFR ? (
          <>
            <P>
              <strong className="text-[var(--gai-black)]">Chers lecteurs, chers partenaires, chers acteurs de l'Afrique et de sa diaspora,</strong>
            </P>
            <P>
              Bienvenue dans <strong translate="no">Global Afrique International</strong>, le Magazine d'Affaires Panafricain.
            </P>
            <P>
              Notre ambition est de créer un espace médiatique moderne, professionnel et engagé,
              dédié à celles et ceux qui font avancer l'Afrique, sa diaspora et ses relations avec le monde.
            </P>
            <P>
              À travers nos articles, interviews, portraits, analyses et dossiers, nous souhaitons mettre
              en lumière les entrepreneurs, dirigeants, institutions, investisseurs, personnalités,
              talents et initiatives qui participent au développement et au rayonnement du continent africain.
            </P>
            <P>
              <span translate="no">Global Afrique International</span> se veut également un trait d'union entre
              l'Afrique et sa diaspora. Nous croyons en la puissance des échanges, des compétences,
              des investissements, des partenariats et des idées pour construire une Afrique plus forte,
              plus visible et davantage connectée aux opportunités internationales.
            </P>

            <H2>Communication &amp; Régie</H2>
            <P>
              Notre département <strong>Communication &amp; Régie</strong> accompagne également les entreprises,
              institutions, organisations et personnalités dans leur visibilité et leur stratégie de communication.
            </P>
            <P>
              Nous proposons un accompagnement adapté à leurs besoins : communication institutionnelle,
              visibilité médiatique, publicité, publireportage, couverture d'événements, contenus éditoriaux,
              communication digitale et valorisation d'image.
            </P>
            <P>
              Notre engagement est simple : <strong className="text-[var(--gai-black)]">donner de la visibilité à ceux qui construisent, entreprennent, innovent et contribuent au rayonnement de l'Afrique.</strong>
            </P>

            <H2>Une ligne éditoriale, six valeurs</H2>
            <P>
              Cette ligne éditoriale repose sur des valeurs qui nous sont essentielles :
              <strong className="text-[var(--gai-black)]"> professionnalisme, crédibilité, excellence, engagement, diversité et respect.</strong>
            </P>
            <div className="my-8 grid grid-cols-2 md:grid-cols-3 gap-3" aria-hidden>
              {["Professionnalisme", "Crédibilité", "Excellence", "Engagement", "Diversité", "Respect"].map((v) => (
                <div key={v} className="p-4 border border-[var(--gai-grey-200)] bg-[var(--gai-white)] text-center">
                  <div className="serif text-lg text-[var(--gai-black)]">{v}</div>
                </div>
              ))}
            </div>
            <P>
              Nous voulons faire de <span translate="no">Global Afrique International</span> une plateforme
              de référence pour l'information, les affaires, les opportunités et les initiatives qui façonnent
              l'Afrique d'aujourd'hui et de demain.
            </P>
            <P>
              Merci de nous accompagner dans cette aventure éditoriale et panafricaine.
            </P>
          </>
        ) : (
          <>
            <P>
              <strong className="text-[var(--gai-black)]">Dear readers, dear partners, dear players of Africa and its diaspora,</strong>
            </P>
            <P>
              Welcome to <strong translate="no">Global Afrique International</strong>, the Pan-African Business Magazine.
            </P>
            <P>
              Our ambition is to create a modern, professional and committed media space, dedicated to
              those who move Africa, its diaspora and its relationship with the world forward.
            </P>
            <P>
              Through our articles, interviews, portraits, analyses and features, we aim to spotlight
              the entrepreneurs, leaders, institutions, investors, personalities, talents and initiatives
              that drive the development and influence of the African continent.
            </P>
            <P>
              <span translate="no">Global Afrique International</span> is also intended as a bridge between
              Africa and its diaspora. We believe in the power of exchange, skills, investment,
              partnerships and ideas to build a stronger, more visible Africa, better connected to
              international opportunities.
            </P>

            <H2>Communication &amp; Régie</H2>
            <P>
              Our <strong>Communication &amp; Régie</strong> department supports companies, institutions,
              organisations and personalities in their visibility and communication strategy.
            </P>
            <P>
              We provide tailored support: institutional communication, media visibility, advertising,
              advertorials, event coverage, editorial content, digital communication and image building.
            </P>
            <P>
              Our commitment is simple: <strong className="text-[var(--gai-black)]">to give visibility to those who build, undertake, innovate and contribute to Africa's influence.</strong>
            </P>

            <H2>An editorial line, six values</H2>
            <P>
              This editorial line rests on values that are essential to us:
              <strong className="text-[var(--gai-black)]"> professionalism, credibility, excellence, commitment, diversity and respect.</strong>
            </P>
            <div className="my-8 grid grid-cols-2 md:grid-cols-3 gap-3" aria-hidden>
              {["Professionalism", "Credibility", "Excellence", "Commitment", "Diversity", "Respect"].map((v) => (
                <div key={v} className="p-4 border border-[var(--gai-grey-200)] bg-[var(--gai-white)] text-center">
                  <div className="serif text-lg text-[var(--gai-black)]">{v}</div>
                </div>
              ))}
            </div>
            <P>
              We want <span translate="no">Global Afrique International</span> to be a reference platform for
              information, business, opportunities and initiatives that shape Africa today and tomorrow.
            </P>
            <P>
              Thank you for joining us on this pan-African editorial journey.
            </P>
          </>
        )}

        {/* SIGNATURE */}
        <div className="mt-16 pt-10 border-t border-[var(--gai-grey-200)]">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="w-24 h-[2px] bg-[var(--gai-red)]" aria-hidden />
            <div>
              <div className="text-[0.72rem] tracking-[0.22em] uppercase text-[var(--gai-grey-500)]">
                {isFR ? "La Direction" : "The Leadership"}
              </div>
              <div className="serif text-2xl text-[var(--gai-black)] mt-2" translate="no">
                Global Afrique International
              </div>
              <div className="text-sm text-[var(--gai-grey-700)] italic">
                {isFR ? "Magazine d'Affaires Panafricain" : "Pan-African Business Magazine"}
              </div>
              <div className="mt-4 pt-4 border-t border-[var(--gai-grey-200)] inline-block pr-10">
                <div className="serif text-xl text-[var(--gai-black)]" translate="no">
                  Bijoux Milton Kilandamoko
                </div>
                <div className="text-[0.72rem] tracking-[0.2em] uppercase text-[var(--gai-red)] mt-1">
                  {isFR ? "Fondatrice & Éditrice" : "Founder & Publisher"}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 flex items-center gap-4">
            <Logo size={54} />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/magazine" className="gai-btn-gold" data-testid="edito-cta-magazine">
              {isFR ? "Feuilleter l'Édition N°0" : "Read Edition N°0"}
            </Link>
            <Link to="/annoncer" className="gai-btn-outline" data-testid="edito-cta-advertise">
              {isFR ? "Annoncer dans GAI" : "Advertise in GAI"}
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
